import { useCallback, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { useConfirm } from "@/shared/hooks/use-confirm";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { SiteBlock, SiteContent, Template } from "../../api/types";
import {
  blockFields,
  blockLabel,
  getAt,
  removeRepeatBindings,
  setAt,
  updateBlock,
} from "../../model/content";
import type { DraftEditor } from "../hooks/useDraftEditor";
import { FieldEditor } from "./editor/FieldEditor";

/**
 * 当前区块的表单:模板 schema 里 `pages.{slug}.{type}.*` 的字段(标量 + 重复组),值落 `block.props`。
 * 模板没给这页这块字段(产品页的 hero、产品精选块的内容来自 products[])就说没有,不替它编。
 *
 * ★ 所有写都走编辑器的函数式口(`applyAt` / `edit`),带上 `block.id`:AI 重写在路上时用户换了区块或改了
 *   别的格,回来的值照样落到发起时那一块、那一格,别的改动不丢。
 * ★ 重复组每项的 React key 是本地生成的(加项时发一个新号,删项时摘掉那个号),不用下标:删掉第 2 项后
 *   第 3 项的输入框不会继承第 2 项的焦点 / 光标。key 不写进 site.json。
 * ★ 删一项先确认(内容会一起删掉,保存草稿后才生效)。
 */
export function BlockForm({
  template,
  content,
  pageSlug,
  block,
  editor,
}: {
  template: Template;
  content: SiteContent;
  pageSlug: string;
  block: SiteBlock;
  editor: Pick<DraftEditor, "applyAt" | "edit" | "rewrite">;
}) {
  const confirm = useConfirm();
  const { prefix, scalars, repeat } = blockFields(template, pageSlug, block.type);
  const blockId = block.id;

  const rawItems = repeat ? getAt(block.props, repeat.listKey) : undefined;
  const items: Array<Record<string, unknown>> = Array.isArray(rawItems)
    ? rawItems.map((x) =>
        x != null && typeof x === "object" ? (x as Record<string, unknown>) : {}
      )
    : [];
  const full = repeat?.max != null && items.length >= repeat.max;
  const itemKeys = useItemKeys(`${blockId}:${repeat?.listKey ?? ""}`, items.length);

  const apply = (propPath: string, value: unknown) =>
    editor.applyAt(`${prefix}${propPath}`, value, "user:edit", blockId);

  const rewriteFor = (propPath: string) => async (instruction: string, current: string) => {
    const path = `${prefix}${propPath}`;
    const v = await editor.rewrite(path, instruction, current);
    if (v !== null) editor.applyAt(path, v, "ai:rewrite", blockId);
    return v !== null;
  };

  const addItem = () => {
    if (!repeat) return;
    editor.edit((prev) =>
      updateBlock(prev, pageSlug, blockId, (props, bindings) => {
        const list = getAt(props, repeat.listKey);
        const cur = Array.isArray(list) ? list : [];
        return { props: setAt(props, repeat.listKey, [...cur, {}]), bindings };
      })
    );
  };

  const removeItem = async (i: number) => {
    if (!repeat) return;
    const ok = await confirm({
      title: `删除第 ${i + 1} 项?`,
      description: "这一项的内容会一起删掉,保存草稿后才生效。",
      confirmText: "删除",
      variant: "destructive",
    });
    if (!ok) return;
    itemKeys.removeAt(i);
    editor.edit((prev) =>
      updateBlock(prev, pageSlug, blockId, (props, bindings) => {
        const list = getAt(props, repeat.listKey);
        const cur = Array.isArray(list) ? list : [];
        return {
          props: setAt(
            props,
            repeat.listKey,
            cur.filter((_, j) => j !== i)
          ),
          bindings: removeRepeatBindings(bindings, `${prefix}${repeat.listKey}`, i),
        };
      })
    );
  };

  return (
    <section
      aria-label="区块表单"
      className={cn("space-y-4 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <h2 className="text-sm font-semibold text-foreground">
        {blockLabel(block.type)} · <code className="font-mono text-2xs">{block.type}</code>
      </h2>

      {scalars.length === 0 && !repeat && (
        <p className="text-xs text-muted-foreground">
          这个区块没有可编辑字段:内容来自资料(产品 / 案例 / 资质)或模板默认,预览里照样能看。
        </p>
      )}

      {scalars.map((f) => (
        <FieldEditor
          key={f.path}
          id={`${blockId}-${f.key}`}
          field={f}
          path={`${prefix}${f.key}`}
          value={getAt(block.props, f.key)}
          binding={content.bindings[`${prefix}${f.key}`]}
          onChange={(v) => apply(f.key, v)}
          onRewrite={rewriteFor(f.key)}
        />
      ))}

      {repeat && (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div
              key={itemKeys.keys[i]}
              role="group"
              aria-label={`第 ${i + 1} 项`}
              className="space-y-2 rounded-md border border-border/70 p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">第 {i + 1} 项</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`删除第 ${i + 1} 项`}
                  className="text-destructive hover:text-destructive"
                  onClick={() => void removeItem(i)}
                >
                  <Trash2 />
                </Button>
              </div>
              {repeat.fields.map((f) => {
                const propPath = `${repeat.listKey}[${i}].${f.key}`;
                return (
                  <FieldEditor
                    key={f.path}
                    id={`${blockId}-${repeat.listKey}-${itemKeys.keys[i]}-${f.key}`}
                    field={f}
                    path={`${prefix}${propPath}`}
                    value={getAt(item, f.key)}
                    binding={content.bindings[`${prefix}${propPath}`]}
                    onChange={(v) => apply(propPath, v)}
                    onRewrite={rewriteFor(propPath)}
                  />
                );
              })}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={full}
            title={full && repeat.max != null ? `最多 ${repeat.max} 项` : undefined}
            onClick={addItem}
          >
            <Plus />
            添加一项
          </Button>
        </div>
      )}
    </section>
  );
}

interface ItemKeyState {
  scope: string;
  keys: string[];
  /** 发到第几号了(只增不减:删掉的号不会被新加的项捡回去)。 */
  seq: number;
}

/** 纯函数:按 scope / 个数对齐 key 表 —— scope 变了整套重发;少了在尾巴补新号;多了截尾。没变原样返回。 */
function fitItemKeys(s: ItemKeyState, scope: string, count: number): ItemKeyState {
  if (s.scope === scope && s.keys.length === count) return s;
  const keys = (s.scope === scope ? s.keys : []).slice(0, count);
  let seq = s.seq;
  while (keys.length < count) {
    seq += 1;
    keys.push(`item-${seq}`);
  }
  return { scope, keys, seq };
}

/**
 * 重复组每项的本地 key:个数变多就补新号,变少就截尾;删项前先 `removeAt(i)` 摘掉那一个号,
 * 后面的项保住自己的 key。`scope`(区块 id + 列表键)变了整套重发。
 * ★ key 表放 state 里,渲染时不改任何 ref:个数 / scope 对不上时按 React「随 props 调整 state」的写法
 *   在渲染中 `setState` 对齐一次(React 丢掉这一轮、立刻用新 state 重渲染);`removeAt` 与删内容的那次
 *   `edit` 同批提交,下一轮个数正好对上,不会补号。
 */
function useItemKeys(scope: string, count: number) {
  const [state, setState] = useState<ItemKeyState>(() =>
    fitItemKeys({ scope, keys: [], seq: 0 }, scope, count)
  );
  const fitted = fitItemKeys(state, scope, count);
  if (fitted !== state) setState(fitted);
  const removeAt = useCallback(
    (i: number) => setState((s) => ({ ...s, keys: s.keys.filter((_, j) => j !== i) })),
    []
  );
  return { keys: fitted.keys, removeAt };
}
